import { useRef, useState } from 'react';
import { MdFolder, MdDescription, MdDelete } from 'react-icons/md';
import Loading from '@/components/Loading';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import CloudUploadIcon from './CloudUploadIcon';
import useUploadPersonaFile, { type ResumeField } from './useUploadPersonaFile';

// 受け付けるファイル形式（ExtractTextService が抽出できる形式）
const ACCEPT =
  'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain';

type Props = {
  fileName: string | null;
  onChange: (value: { fileName: string; fields: ResumeField[] } | null) => void;
};

export default function ResumeUploadStep({ fileName, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingName, setPendingName] = useState<string | null>(null);
  const upload = useUploadPersonaFile();

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setPendingName(file.name);
    upload.mutate(file, {
      onSuccess: (data) => {
        onChange({ fileName: data.filename, fields: data.fields });
        setPendingName(null);
      },
      onError: (error) => {
        showErrorToast(
          error instanceof Error ? error.message : 'ファイルのアップロードに失敗しました'
        );
        setPendingName(null);
      },
    });
  };

  const hasFile = !!fileName;

  return (
    <div className="flex flex-col gap-lg">
      <div className="flex flex-col gap-xs">
        <p className="text-heading-xxs text-main">履歴書をアップロード</p>

        {upload.isPending ? (
          <div className="flex items-center gap-sm rounded-sm border border-solid border-border bg-white p-lg">
            <Loading size="small" />
            <span className="truncate text-body-sm text-main">
              {pendingName} をアップロード中...
            </span>
          </div>
        ) : hasFile ? (
          <div className="flex items-center justify-between gap-sm rounded-sm border border-solid border-border bg-white p-lg">
            <div className="flex min-w-0 items-center gap-sm">
              <MdDescription size={32} className="shrink-0 text-main" />
              <span className="truncate text-body-sm text-main">{fileName}</span>
            </div>
            <button
              type="button"
              onClick={() => onChange(null)}
              className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-solid border-danger text-danger hover:bg-danger_lighten"
              aria-label="削除"
            >
              <MdDelete size={16} />
            </button>
          </div>
        ) : (
          <div
            className={
              'flex h-56 flex-col items-center justify-center gap-sm rounded-sm border-2 border-dashed p-lg ' +
              (isDragging ? 'border-primary bg-primary_lighten' : 'border-border bg-gray-50')
            }
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              handleFiles(e.dataTransfer.files);
            }}
          >
            <CloudUploadIcon size={48} className="text-primary" />
            <div className="flex flex-col items-center gap-xxs text-main">
              <p className="text-body-lg">履歴書をドラッグアンドドロップ</p>
              <p className="text-body-md">または</p>
            </div>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex h-10.5 items-center gap-xxs rounded-sm border border-solid border-border bg-white px-sm text-body-sm font-bold text-main hover:bg-gray-50"
            >
              <MdFolder className="size-icon-xs" />
              ファイルを選択
            </button>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
